import { useEffect, useState } from "react";
import Router from "next/router";
import Tealium from "@4tn/webx-analytics";
import { styled } from "@mui/material/styles";
import { HamburgerIcon, SearchIcon } from "@common/Icons";
import { maxPageContentWidth, searchInputPlaceholderText } from "@constants/consts";
import { colors, fonts } from "@constants/cssVariables";
import { TEALIUM_EVENT_CATEGORY, TEALIUM_EVENT_LABEL, TEALIUM_EVENT_NAME } from "@constants/tealium.constants";
import updateSearchParams from "@utils/navigation/updateSearchParams";
import BrandLogo from "./BrandLogo";

export const topBarHamburgerAriaLabel = "menu";
export const topBarBrandLogoAriaLabel = "home";
export const topBarSearchIconAriaLabel = "search";

export const TopBarContainer = styled("div")(({ theme }) => ({
  zIndex: theme.zIndex.appBar,
  position: "absolute",
  top: 0,
  width: "100%",
  height: 56,
  backgroundColor: colors.header,
  padding: theme.spacing(0, 4),

  [theme.breakpoints.up("md")]: {
    height: 64,
    padding: 0,
  },
}));

const TopBarContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: "100%",
  maxWidth: maxPageContentWidth,
  margin: "0 auto",

  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(0, 8),
  },

  [theme.breakpoints.up("lg")]: {
    justifyContent: "flex-start",
    padding: theme.spacing(0, 24),
    gap: theme.spacing(6),
  },
}));

const TopBarHamburgerMenu = styled(HamburgerIcon)(() => ({
  cursor: "pointer",
  svg: {
    display: "flex",
    width: 32,
    height: 32,
    color: colors.neutral0,
  },
}));

const TopBarSearchInput = styled("div")(({ theme }) => ({
  display: "none",

  [theme.breakpoints.up("lg")]: {
    cursor: "text",
    display: "flex",
    alignItems: "center",
    position: "absolute",
    left: "calc(50% - 200px)",
    width: 400,
    height: 40,
    borderRadius: 100,
    padding: theme.spacing(2, 11),
    // We use rgba format here in order to apply opacity exclusively to the background color
    backgroundColor: "rgba(255,255,255, 0.2)",
    color: colors.neutral0,
    font: fonts.bodyMedium,
    outline: "none",
    ":empty:before": {
      // Allows us to use the placeholder prop for our styled div, just like we would for a native input
      content: "attr(placeholder)",
    },
  },
}));

const TopBarSearchIcon = styled(SearchIcon)(({ theme }) => ({
  cursor: "pointer",
  svg: {
    display: "flex",
    color: colors.neutral0,
    width: 32,
    height: 32,
  },

  [theme.breakpoints.up("lg")]: {
    cursor: "text",
    position: "absolute",
    // 200px -> 50% of the search input
    // 12px -> 50% of the icon width
    left: "calc(50% - 200px + 12px)",
    svg: {
      width: 24,
      height: 24,
    },
  },
}));

const trackClick = (eventLabel: string) => {
  Tealium.link({
    event_category: TEALIUM_EVENT_CATEGORY.HEADER,
    event_name: TEALIUM_EVENT_NAME.HEADER_CLICK,
    event_label: eventLabel,
  });
};

const handleBrandLogoClick = () => {
  trackClick(TEALIUM_EVENT_LABEL.NAVIGATION.HOME);
  if (Router.pathname !== "/") {
    Router.push("/");
  }
};

const TopBar = () => {
  const [isHamburgerMenuOpen, setIsHamburgerMenuOpen] = useState(false);

  useEffect(() => {
    const onHamburgerMenu = (data: WebXEventParams["hamburgerMenu"]) => setIsHamburgerMenuOpen(data.isOpen);
    window.eventBus.on("hamburgerMenu", onHamburgerMenu);

    return () => window.eventBus.off("hamburgerMenu", onHamburgerMenu);
  }, []);

  const handleSearchClick = () => {
    updateSearchParams("");
    trackClick(TEALIUM_EVENT_LABEL.NAVIGATION.SEARCH);
  };

  const handleHamburgerMenuClick = () => {
    window.eventBus.dispatch("hamburgerMenu", { isOpen: !isHamburgerMenuOpen });
  };

  return (
    <TopBarContainer>
      <TopBarContentWrapper>
        <TopBarHamburgerMenu role="button" aria-label={topBarHamburgerAriaLabel} onClick={handleHamburgerMenuClick} />
        <BrandLogo
          role="button"
          aria-label={topBarBrandLogoAriaLabel}
          cursor="pointer"
          onClick={handleBrandLogoClick}
        />
        <TopBarSearchInput
          role="input"
          contentEditable="true"
          placeholder={searchInputPlaceholderText}
          spellCheck="false"
          onClick={handleSearchClick}
        />
        <TopBarSearchIcon role="button" aria-label={topBarSearchIconAriaLabel} onClick={handleSearchClick} />
      </TopBarContentWrapper>
    </TopBarContainer>
  );
};

export default TopBar;
